
import { Observable, of, from } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { catchError, map, timeout } from 'rxjs/operators';
import { Firestore, collection, doc, addDoc, setDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';

import { QuestionService } from '_services/question.service';
import { UserService } from '_services/user.service';
import { AppCheckErrorService } from './app-check-error.service';

import { Answer } from '_models/question';
import { Result, AdminResult } from '_models/result';


export interface Session {
  univName: string;
  userId: string;
  cbUserId: string;
  cbPersonId?: string;
  savedAt: Date;
  expireAt?: Date;
  answers: Answer[];
  sessionName?: string;
  result?: Result | AdminResult;
}

const DAY_MS = 24 * 60 * 60 * 1000;
// Session lookups are light Firestore reads, so this only ever trips on a cold
// start or a dropped connection. Without it a hung callable never emits and the
// caller waits forever — which for the resume flow is a spinner the user cannot
// escape. Well under the callable SDK's own ~70s default so we fail first.
const SESSION_CALL_TIMEOUT_MS = 20_000;
// Retention windows per collection (see docs/session-collection-split-plan.md).
const RETENTION_DAYS = { userSessions: 365, guestSessions: 30 } as const;

@Injectable()
export class SessionService {
  private afs = inject(Firestore);
  private fns = inject(Functions);
  private us = inject(UserService);
  private qs = inject(QuestionService);
  private appCheckErrorService = inject(AppCheckErrorService);


  // Get a session from firestore by calling a google cloud function
  public getSessionFromCloud$(sessionId: string | null, cbUserId?: string): Observable<any> {
    console.log(sessionId ? 'Loading Session = ' + sessionId : 'Loading Session by CB ID = ' + cbUserId);
    const callable = httpsCallable(
      this.fns,
      this.us.isAdmin ? 'getAdminSession' : 'getSession'
    );
    const payload = this.us.isAdmin ? { sessionId } : { sessionId, cbUserId };
    return from(callable(payload)).pipe(
      timeout(SESSION_CALL_TIMEOUT_MS),
      map((result: any) => result.data),
      // tap((session) => { console.log('Session from cloud function:', session); }),
      catchError((error) => {
        console.error('Error getting session from cloud function:', error);
        return of(null);
      })
    );
  }

  // Claim a pending session on return from College Board login. The function
  // reads pendingSessions/{id}, creates a NEW userSessions doc with the CB
  // identity stamped server-side, and returns the promoted session data.
  // Returns null on quiet failure (missing/expired pending doc, or no CB identity).
  public claimPendingSession$(pendingSessionId: string): Observable<any> {
    const callable = httpsCallable(this.fns, 'getSession');
    return from(callable({
      sessionId: pendingSessionId,
      pending: true,
      cbUserId: this.us.cbUserId,
      cbPersonId: this.us.cbPersonId,
    })).pipe(
      // Emits null on timeout, which the caller already treats as a failed
      // claim — so a hung function lands the user on the calculator rather
      // than on an endless spinner.
      timeout(SESSION_CALL_TIMEOUT_MS),
      map((result: any) => result.data),
      catchError((error) => {
        console.error('Error claiming pending session:', error);
        return of(null);
      })
    );
  }

  // Updates the answers in the live questions with answers from a retrieved session
  public setAnswers(session: Session) {
    if (session) {
      for (const q of session.answers) {

        if (!this.qs.setAnswer(q.questionId, q.value)) {
          // If the question or answer is not found, clear the answer
          q.value = '';
        }

      }
    }
    this.qs.bootstrapCompoundFromLegacySession();
    this.qs.updateQuestionStates();
  }


  // Checks to see if the answers are different from the session that was loaded
  // Called from the Results Component
  public isNewSession(schoolId: string, answers: Answer[]): boolean {
    // No id means nowhere to write back to, so a new doc is required regardless
    // of whether the answers themselves changed.
    if (this.us.sessionId === '') {
      return true;
    }
    return !this.matchesLoadedSession(schoolId, answers);
  }

  // True when these answers are the same school and the same answers as the
  // session that was loaded or restored. Deliberately ignores sessionId: the
  // restore paths hold no id (see WelcomeService), so this answers "is this the
  // same work?" rather than "have we somewhere to write it back to?".
  public matchesLoadedSession(schoolId: string, answers: Answer[]): boolean {
    const loaded = this.us.loadedSession;
    if (!loaded?.answers || loaded.univName !== schoolId) {
      return false;
    }
    return !this.areArraysDifferent(answers, loaded.answers);
  }

  // Compares two arrays of answers
  private areArraysDifferent(arr1: Answer[], arr2: Answer[]): boolean {
    // Check if arrays have the same length
    if (arr1.length !== arr2.length) {
      return true;
    }

    // Sort the arrays by id (copy first to avoid mutating caller's arrays)
    const sorted1 = [...arr1].sort((a, b) => a.questionId.localeCompare(b.questionId));
    const sorted2 = [...arr2].sort((a, b) => a.questionId.localeCompare(b.questionId));

    // Compare each element
    for (let i = 0; i < sorted1.length; i++) {
      if (sorted1[i].questionId !== sorted2[i].questionId || sorted1[i].value !== sorted2[i].value) {
        return true;
      }
    }

    return false;
  }



  // Picks the collection a session is written to, based on CB identity.
  // Fail toward retention: any CB signal -> userSessions, so real user data
  // is never dropped by the 30-day guest TTL by mistake.
  private sessionCollectionName(): 'sessions_admin' | 'userSessions' | 'guestSessions' {
    if (this.us.isAdmin) {
      return 'sessions_admin';
    }
    const hasCbIdentity = !!this.us.cbPersonId
      || (!!this.us.cbUserId && this.us.cbUserId !== 'guest');
    return hasCbIdentity ? 'userSessions' : 'guestSessions';
  }

  public storeSession(answers: Answer[]): Promise<string> {
    const collectionName = this.sessionCollectionName();
    const sessionCollection = collection(this.afs, collectionName);
    const savedAt = new Date();
    const docData: Session = {
      univName: this.us.schoolId,
      userId: this.us.userId,
      cbUserId: this.us.cbUserId,
      cbPersonId: this.us.cbPersonId,
      savedAt,
      answers: answers,
      ...(this.us.loadedSession?.sessionName ? { sessionName: this.us.loadedSession.sessionName } : {}),
    };
    // sessions_admin has no TTL policy, so it gets no expireAt.
    if (collectionName !== 'sessions_admin') {
      docData.expireAt = new Date(savedAt.getTime() + RETENTION_DAYS[collectionName] * DAY_MS);
    }

    return addDoc(sessionCollection, docData)
      .then(docRef => {
        console.log('Set ID', docRef.id);
        this.us.sessionId = docRef.id;
        this.us.loadedSession = docData;
        return docRef.id;
      })
      .catch(error => {
        this.appCheckErrorService.reportIfAppCheckError(error, 'Firestore', {
          operation: 'storeSession',
          schoolId: this.us.schoolId,
          assumeAppCheck: true
        });
        throw error;
      });
  }

  public updateSessionName(sessionId: string, sessionName: string): Promise<void> {
    const sessionCollection = collection(this.afs, 'sessions_admin');
    const docRef = doc(sessionCollection, sessionId);
    return setDoc(docRef, { sessionName }, { merge: true })
      .then(() => {
        if (this.us.loadedSession) {
          this.us.loadedSession = { ...this.us.loadedSession, sessionName };
        }
      })
      .catch(error => {
        this.appCheckErrorService.reportIfAppCheckError(error, 'Firestore', {
          operation: 'updateSessionName',
          schoolId: this.us.schoolId,
          assumeAppCheck: true
        });
        throw error;
      });
  }

  // Mint a guestSessions clone for Copy link / Email. Always guest — never the
  // live userSessions id — so a shared URL cannot point at a CB-stamped,
  // year-retained account session. Does not change the sharer's sessionId.
  public createGuestShareClone(answers: Answer[], result?: Result | AdminResult): Promise<string> {
    const savedAt = new Date();
    const docData: Record<string, unknown> = {
      univName: this.us.schoolId,
      userId: this.us.userId,
      cbUserId: 'guest',
      savedAt,
      expireAt: new Date(savedAt.getTime() + RETENTION_DAYS.guestSessions * DAY_MS),
      answers,
    };
    const shareResult = this.stripNextSteps(result ?? this.us.loadedSession?.result);
    if (shareResult) {
      docData.result = shareResult;
    }

    return addDoc(collection(this.afs, 'guestSessions'), docData)
      .then(docRef => docRef.id)
      .catch(error => {
        this.appCheckErrorService.reportIfAppCheckError(error, 'Firestore', {
          operation: 'createGuestShareClone',
          schoolId: this.us.schoolId,
          assumeAppCheck: true
        });
        throw error;
      });
  }

  // Snapshot in-progress answers just before a College Board login redirect,
  // so they survive the same-tab round-trip and can be claimed on return.
  // No CB identity is stamped here — the point is that the user is logging in;
  // identity is attached server-side at claim time. Short 7-day TTL if abandoned.
  public storePendingSession(answers: Answer[], result?: Result | AdminResult): Promise<string> {
    const savedAt = new Date();
    const pendingCollection = collection(this.afs, 'pendingSessions');
    const docData: Record<string, unknown> = {
      univName: this.us.schoolId,
      userId: this.us.userId,
      savedAt,
      expireAt: new Date(savedAt.getTime() + 7 * DAY_MS),
      answers,
    };
    if (result) {
      docData.result = result;
    }

    return addDoc(pendingCollection, docData)
      .then(docRef => {
        return docRef.id;
      })
      .catch(error => {
        this.appCheckErrorService.reportIfAppCheckError(error, 'Firestore', {
          operation: 'storePendingSession',
          schoolId: this.us.schoolId,
          assumeAppCheck: true
        });
        throw error;
      });
  }

  // Store the results to the previously stored session
  public storeResults(res: any) {
    // Same picker as storeSession so the result merges onto the doc it was created in.
    const sessionCollection = collection(this.afs, this.sessionCollectionName());

    if (this.us.sessionId !== '') {
      const docRef = doc(sessionCollection, this.us.sessionId);
      if (this.us.isAdmin) {
        void setDoc(docRef, { result: res }, { merge: true }).catch(error => {
          this.appCheckErrorService.reportIfAppCheckError(error, 'Firestore', {
            operation: 'storeResultsAdmin',
            schoolId: this.us.schoolId,
            assumeAppCheck: true
          });
        });
      } else {
        const result = (res.npcResult) ? res.npcResult : res;
        // Remove nextSteps prior to saving (the calc function passes population specific next steps that we don't want to persist)
        result.nextSteps = '';
        void setDoc(docRef, { result: result }, { merge: true }).catch(error => {
          this.appCheckErrorService.reportIfAppCheckError(error, 'Firestore', {
            operation: 'storeResults',
            schoolId: this.us.schoolId,
            assumeAppCheck: true
          });
        });
      }
    }
  }

  private stripNextSteps(result: Result | AdminResult | undefined): Result | AdminResult | undefined {
    if (!result) {
      return undefined;
    }
    const npc = 'npcResult' in result && result.npcResult ? result.npcResult : result;
    if (npc && 'nextSteps' in npc) {
      return { ...npc, nextSteps: '' } as Result;
    }
    return result;
  }

}





